import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { Disclaimer } from "../components/Disclaimer";
import { UpgradeBanner } from "../components/Upgrade";
import { LocationEditor } from "../components/LocationEditor";
import { MapPin, ScanLine, Ruler, ArrowRight, ListChecks, Edit3, Sparkles, MessageSquare, Lock } from "lucide-react";

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [session, setSession] = useState(undefined);
  const [tasks, setTasks] = useState([]);
  const [editLoc, setEditLoc] = useState(false);

  const load = async () => {
    const { data } = await api.get("/sessions/active");
    setSession(data);
    if (data) {
      const res = await api.get(`/sessions/${data.id}`);
      setTasks(res.data.tasks);
    }
  };
  useEffect(() => { load(); }, []);

  const place = user?.city ? `${user.city}${user.region ? ", " + user.region : ""}` : null;

  return (
    <Layout>
      <div className="fadeup space-y-6">
        {/* Place label */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono">Suggestions for</div>
            <div className="flex items-center gap-2 mt-1">
              <MapPin className="w-5 h-5" style={{ color: "var(--earn)" }} />
              <span className="font-head font-bold text-xl" data-testid="place-label">{place || "No location set"}</span>
              <button onClick={() => setEditLoc(!editLoc)} data-testid="edit-location-btn" className="ml-1"><Edit3 className="w-4 h-4" style={{ color: "var(--text-2)" }} /></button>
            </div>
          </div>
          {user?.is_guest && <span className="badge" style={{ background: "var(--cost)" }}>Guest mode</span>}
        </div>

        {!user?.paid && <UpgradeBanner />}

        {(editLoc || !place) && (
          <LocationEditor onDone={() => setEditLoc(false)} onClose={place ? () => setEditLoc(false) : undefined} />
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button onClick={() => nav(user?.paid ? "/ask" : "/paywall")} data-testid="home-ask-btn" className="pm-card p-6 text-left hover:shadow-[4px_4px_0_rgba(10,10,10,0.15)] transition-all relative">
            {!user?.paid && <Lock className="w-4 h-4 absolute top-4 right-4" style={{ color: "var(--text-2)" }} />}
            <MessageSquare className="w-7 h-7 mb-3" />
            <div className="font-head font-bold text-lg">Talk it through</div>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{user?.paid ? "Just chat with the mentor. No path needed." : "Members only — chat with the mentor."}</p>
          </button>
          <button onClick={() => nav("/scan")} data-testid="home-scan-btn" className="pm-card p-6 text-left hover:shadow-[4px_4px_0_rgba(10,10,10,0.15)] transition-all">
            <ScanLine className="w-7 h-7 mb-3" />
            <div className="font-head font-bold text-lg">Scan anything</div>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Point your camera at a thing. Get local earn ideas.{!user?.paid && " (1 free)"}</p>
          </button>
          <button onClick={() => nav("/measure")} data-testid="home-measure-btn" className="pm-card p-6 text-left hover:shadow-[4px_4px_0_rgba(10,10,10,0.15)] transition-all">
            <Ruler className="w-7 h-7 mb-3" />
            <div className="font-head font-bold text-lg">Measure a thing</div>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Cost to fix vs money you might make here.{!user?.paid && " (1 free)"}</p>
          </button>
        </div>

        {/* Active session (members) */}
        {!user?.paid ? (
          <div className="pm-card p-6 text-center" data-testid="locked-mentor-card">
            <Lock className="w-7 h-7 mx-auto mb-3" />
            <div className="font-head font-bold text-lg">Unlock the full mentor</div>
            <p className="text-sm mt-1 mb-4" style={{ color: "var(--text-2)" }}>Guided paths, weekly steps, saved sessions, invoices and mentor chat are member features.</p>
            <button onClick={() => nav("/paywall")} className="pm-btn" data-testid="home-upgrade-btn">See plans</button>
          </div>
        ) : session === undefined ? null : session ? (
          <div className="pm-card p-6" data-testid="active-session-card">
            <div className="label-mono mb-1">Active path — for {session.placeSnapshot?.city || place}</div>
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-head font-bold text-xl">{session.title}</h3>
            </div>
            <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{session.summary}</p>
            {tasks.length > 0 && (
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center gap-2 label-mono"><ListChecks className="w-4 h-4" /> This week</div>
                {tasks.filter((t) => t.weekNumber === 1).slice(0, 3).map((t) => (
                  <div key={t.id} className="text-sm flex items-center gap-2">
                    <span className="badge" style={{ background: t.status === "done" ? "var(--earn)" : t.status === "skipped" ? "var(--text-2)" : "var(--text)" }}>{t.status}</span>
                    {t.title}
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => nav(`/session/${session.id}`)} className="pm-btn mt-4 flex items-center gap-2" data-testid="continue-session-btn">
              Continue mentor <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="pm-card p-6 text-center" data-testid="no-session-card">
            <Sparkles className="w-7 h-7 mx-auto mb-3" />
            <div className="font-head font-bold text-lg">No active path yet</div>
            <p className="text-sm mt-1 mb-4" style={{ color: "var(--text-2)" }}>Answer a few questions and we'll suggest 2–3 local paths.</p>
            <button onClick={() => nav("/intake")} className="pm-btn" data-testid="start-intake-btn">Start intake</button>
          </div>
        )}

        <Disclaimer />
      </div>
    </Layout>
  );
}
